import {
  formatDate, uploadedAt, formatSize, getCanonicalType, getMediaKindIcon, getTypeIcon,
  getDocCategory, getDocTypeDisplay, getDocExtent, getDocCost, getDocChars,
} from '../hooks/useInventoryData';
import KeyEntitiesBadge from '../components/inventory/KeyEntitiesBadge';
import { docNoteCount } from './notesIndex';
import { scanBadge } from '../constants/scanStatus';
import { cutChildParts, isCutChild, pagesLabel } from '../components/segments/cutChild';
import { docTitle, hasOwnTitle } from '../domain/documentTitle';

// String literals deliberately inlined to break a circular import with
// InventoryContext.jsx (which imports DEFAULT_VISIBLE_COLUMNS from here).
// Keep these in sync with SORT_KEYS in ./inventoryConstants.js.
const SORT = {
  FILENAME: 'filename',
  DATE: 'date',
  SIZE: 'size',
  EXTENT: 'extent',
  COST: 'cost',
  STATUS: 'status',
};

/**
 * inventoryColumns — declarative registry of inventory-table columns.
 *
 * Adding a column = one entry here. Removing = delete the entry.
 * The table reads `visibleColumns` from InventoryContext and renders
 * the column list in registry order.
 *
 * Each column shape:
 *   id              — stable key (URL, storage, dispatch)
 *   header          — column header label
 *   description     — one-liner shown in the column picker
 *   width           — pixel width OR 'flex' (Name only)
 *   align           — 'left' (default) | 'right' | 'center'
 *   sortKey         — SORT_KEYS value if column is sortable
 *   locked          — true means always visible, can't be hidden
 *   defaultVisible  — initial state when no saved prefs/view exist
 *   renderHeader?   — override header rendering (rare)
 *   renderCell      — (doc, ctx) => ReactNode. ctx = { dispatch, hashCountMap }
 *
 * Invariants:
 *   - Name is always first and always locked.
 *   - Cells must be lightweight; the row uses virtualization but holds
 *     the cell closure on every render.
 *   - Cells may dispatch (e.g. badge click → filter); always stop event
 *     propagation so the row-expand click handler doesn't also fire.
 */

const STATUS_BADGES = {
  processing: { label: 'Processing', cls: 'inv-status--processing' },
  pending:    { label: 'Pending',    cls: 'inv-status--queued' },
  failed:     { label: 'Failed',     cls: 'inv-status--error' },
  error:      { label: 'Error',      cls: 'inv-status--error' },
  queued:     { label: 'Queued',     cls: 'inv-status--queued' },
};

function NameCell({ doc, ctx }) {
  const ct = getCanonicalType(doc);
  const icon = getMediaKindIcon(doc.media_kind) || getTypeIcon(ct, doc.document_purpose || doc.document_type);
  const dtDisplay = getDocTypeDisplay(doc);
  const groupDisplay = getDocCategory(doc);
  if (isCutChild(doc)) {
    // A document cut out of a compilation: its own title leads (the generated file name
    // repeats the parent's and truncates before the part that tells the rows apart).
    const { parentStem } = cutChildParts(doc.filename);
    const title = docTitle(doc);
    return (
      <>
        <span className="inv-row-icon" title={ct}>{icon}</span>
        <div className="inv-row-name" data-cut-child={doc.container_id}>
          <span className="inv-row-filename" title={`${title}\n${doc.filename}`}>{title}</span>
          <span className="inv-row-origin" title={`Pages ${doc.page_start}–${doc.page_end} of ${parentStem}`}>
            {pagesLabel(doc)}{parentStem ? ` · in ${parentStem}` : ''}
          </span>
          {dtDisplay && <span className="inv-row-doctype">{dtDisplay}</span>}
        </div>
      </>
    );
  }
  if (doc.is_compilation_container) {
    // The source file steps back: its documents are the rows below it.
    const n = ctx?.cutChildCounts?.get(doc.id) || 0;
    return (
      <>
        <span className="inv-row-icon" title="Source file">🗂</span>
        <div className="inv-row-name" data-cut-container={doc.id}>
          <span className="inv-row-filename">{doc.filename || 'Untitled'}</span>
          <span className="inv-row-origin">Source file{n ? ` · ${n} ${n === 1 ? 'document' : 'documents'} inside` : ''} ▸</span>
        </div>
      </>
    );
  }
  return (
    <>
      <span className="inv-row-icon" title={ct}>{icon}</span>
      <div className="inv-row-name">
        <span className="inv-row-filename" title={hasOwnTitle(doc) ? `${docTitle(doc)}\n${doc.filename}` : doc.filename}>{docTitle(doc)}</span>
        {hasOwnTitle(doc) && <span className="inv-row-origin" title="The file as uploaded">{doc.filename}</span>}
        {groupDisplay && <span className="inv-row-category">{groupDisplay}</span>}
        {dtDisplay && <span className="inv-row-doctype">{dtDisplay}</span>}
      </div>
    </>
  );
}

// Extent — the typed "how much content" measure. Renders unit-bearing when
// present, and distinguishes not-applicable (—) from still-processing (·) so a
// blank cell never conflates "doesn't apply" with "not loaded yet".
function ExtentCell({ doc }) {
  const ext = getDocExtent(doc);
  if (ext.state === 'value') {
    return <span className="inv-row-stats" title={ext.title || undefined}>{ext.display}</span>;
  }
  if (ext.state === 'pending') {
    return <span className="inv-row-stats inv-row-stats--muted" title="Still processing…">·</span>;
  }
  const title = ext.state === 'na'
    ? 'Not applicable for this file type'
    : ext.unit === 'second'
      ? 'Duration not captured for this file — files uploaded before duration capture show a dash'
      : 'No content extracted';
  return <span className="inv-row-stats inv-row-stats--muted" title={title}>—</span>;
}

function SizeCell({ doc }) {
  const bytes = doc.file_size || 0;
  return (
    <span className="inv-row-stats" title={bytes ? `${bytes.toLocaleString()} bytes` : undefined}>
      {formatSize(doc.file_size)}
    </span>
  );
}

function CostCell({ doc }) {
  const { cents, display } = getDocCost(doc);
  return (
    <span
      className={`inv-row-stats${cents > 0 ? '' : ' inv-row-stats--muted'}`}
      title={cents > 0 ? `AI processing cost: ${display}` : 'No processing cost recorded'}
    >
      {display}
    </span>
  );
}

function CharsCell({ doc }) {
  const { value, display } = getDocChars(doc);
  return <span className={`inv-row-stats${value ? '' : ' inv-row-stats--muted'}`}>{display}</span>;
}

function StatusCell({ doc, ctx }) {
  const { dispatch, hashCountMap = {}, onAck, ackBusy, pendingReview } = ctx;
  // Malware scan state supersedes the normal pipeline status badge:
  // "Scanning…" while held, "Malware blocked" instead of a bare "Failed".
  const scan = scanBadge(doc);
  const badge = scan ? null : (STATUS_BADGES[doc.status] || null);
  const dupCount = doc.file_hash_sha256 ? (hashCountMap[doc.file_hash_sha256] || 0) : 0;
  const corrected = doc.classification_source === 'human_manual';
  const indexFailed = doc.index_status === 'failed';

  if (!scan && !badge && !corrected && !indexFailed && dupCount <= 1) {
    return <span className="inv-row-status" />;
  }

  return (
    <span className="inv-row-status">
      {scan && (
        <span className={`inv-status ${scan.invCls}`} title={scan.title}>
          {scan.label}
        </span>
      )}
      {badge && (
        <span
          className={`inv-status ${badge.cls}`}
          title={doc.status === 'failed' ? (doc.processing_error || 'Processing failed') : ''}
        >
          {badge.label}
          {doc.status === 'failed' && (doc.extraction?.error_detail?.failure_category) && (
            <span className="inv-fail-reason"> · {doc.extraction.error_detail.failure_category}</span>
          )}
        </span>
      )}
      {indexFailed && (
        <span className="inv-tier-badge inv-tier--failed" title="Indexing failed">
          ⚠ Index failed
        </span>
      )}
      {corrected && (
        <>
          <span
            className="inv-correction-badge"
            title="Manually reclassified — click to filter to corrections"
            onClick={(e) => {
              e.stopPropagation();
              dispatch({ type: 'ADD_FILTER', axis: 'labelStatus', value: 'corrected' });
            }}
          >
            ✏️ Corrected
          </span>
          {pendingReview && (
            <button
              className="inv-correction-ack-btn"
              onClick={onAck}
              disabled={ackBusy}
              title="Mark this correction reviewed"
            >
              {ackBusy ? '…' : '✓ Reviewed'}
            </button>
          )}
        </>
      )}
      {dupCount > 1 && (
        <span
          className="inv-dup-badge"
          title={dupCount + ' copies of this file (identical content)'}
          onClick={(e) => {
            e.stopPropagation();
            dispatch({ type: 'SET_DUPLICATE_HASH_FILTER', hash: doc.file_hash_sha256 });
          }}
        >
          {dupCount} copies
        </span>
      )}
    </span>
  );
}

function EntitiesCell({ doc }) {
  return <KeyEntitiesBadge docId={doc.id} />;
}

function DateCell({ doc, field = 'created_at' }) {
  return <span className="inv-row-date">{formatDate(field === 'created_at' ? uploadedAt(doc) : doc[field])}</span>;
}

function NotesCell({ doc }) {
  const n = docNoteCount(doc.id);
  if (!n) return <span className="inv-notes-none">—</span>;
  return (
    <span className="inv-notes-badge" title={`${n} notepad clip${n > 1 ? 's' : ''}`}>
      🗒️ {n}
    </span>
  );
}

export const COLUMNS = {
  name: {
    id: 'name',
    header: 'Name',
    description: 'Filename, category, and document type',
    width: 'flex',
    align: 'left',
    sortKey: SORT.FILENAME,
    locked: true,
    defaultVisible: true,
    renderCell: (doc, ctx) => <NameCell doc={doc} ctx={ctx} />,
  },
  extent: {
    id: 'extent',
    header: 'Length',
    description: 'Natural length — pages, duration, sheets, slides, or entries',
    width: 120,
    align: 'right',
    sortKey: SORT.EXTENT,
    defaultVisible: true,
    renderCell: (doc) => <ExtentCell doc={doc} />,
  },
  size: {
    id: 'size',
    header: 'Size',
    description: 'File size on disk',
    width: 90,
    align: 'right',
    sortKey: SORT.SIZE,
    defaultVisible: true,
    renderCell: (doc) => <SizeCell doc={doc} />,
  },
  cost: {
    id: 'cost',
    header: 'Cost',
    description: 'AI processing cost for this document',
    width: 80,
    align: 'right',
    sortKey: SORT.COST,
    defaultVisible: false,
    renderCell: (doc) => <CostCell doc={doc} />,
  },
  chars: {
    id: 'chars',
    header: 'Characters',
    description: 'Extracted text length (text documents only)',
    width: 110,
    align: 'right',
    defaultVisible: false,
    renderCell: (doc) => <CharsCell doc={doc} />,
  },
  status: {
    id: 'status',
    header: 'Status',
    description: 'Processing state, corrections, duplicates, index failures',
    width: 160,
    align: 'center',
    sortKey: SORT.STATUS,
    defaultVisible: true,
    renderCell: (doc, ctx) => <StatusCell doc={doc} ctx={ctx} />,
  },
  entities: {
    id: 'entities',
    header: 'Top entities',
    description: 'People, places, and objects mentioned (from GraphRAG)',
    width: 220,
    align: 'left',
    defaultVisible: true,
    renderCell: (doc) => <EntitiesCell doc={doc} />,
  },
  date: {
    id: 'date',
    header: 'Uploaded',
    description: 'Upload timestamp (when the file was first added)',
    width: 150,
    align: 'left',
    sortKey: SORT.DATE,
    defaultVisible: true,
    renderCell: (doc) => <DateCell doc={doc} field="created_at" />,
  },
  last_processed: {
    id: 'last_processed',
    header: 'Last processed',
    description: 'When the pipeline last finished a processing stage',
    width: 150,
    align: 'left',
    defaultVisible: false,
    renderCell: (doc) => <DateCell doc={doc} field="last_processed_at" />,
  },
  updated: {
    id: 'updated',
    header: 'Updated',
    description: 'Last time the document row was modified (incl. edits, acks)',
    width: 150,
    align: 'left',
    defaultVisible: false,
    renderCell: (doc) => <DateCell doc={doc} field="updated_at" />,
  },
  notes: {
    id: 'notes',
    header: 'Notes',
    description: 'Count of attorney notepad clips on this document',
    width: 90,
    align: 'right',
    defaultVisible: false,
    renderCell: (doc) => <NotesCell doc={doc} />,
  },
};

/** Canonical display order. Name first, then the measure columns
 *  (extent/size/cost/chars), then status/entities, then the three time
 *  columns. Future columns append here; picker order follows. */
export const COLUMN_ORDER = ['name', 'extent', 'size', 'cost', 'chars', 'status', 'entities', 'date', 'last_processed', 'updated', 'notes'];

export function getColumn(id) {
  return COLUMNS[id] || null;
}

/** Floor width (px) for the flexible Name column. Below this the filename +
 *  category + doctype lines crush and wrap; we'd rather scroll horizontally. */
export const NAME_MIN_WIDTH = 240;

/** Layout constants mirroring the header/row chrome in app.css so the computed
 *  table min-width lines up with the real flex layout. */
const TABLE_GAP = 8;        // gap between cells
const TABLE_HPAD = 24;      // 12px left + 12px right padding
const TABLE_CHECKBOX = 24;  // leading select checkbox slot
const TABLE_CHEVRON = 24;   // trailing chevron slot (header)

/** Inline style for a column header or body cell — width + alignment from
 *  the registry entry. Header + body cells use this same fn so they stay
 *  aligned even if the registry changes. The flex Name column carries a
 *  min-width floor so it never crushes below readability; when the visible
 *  columns no longer fit, the table scrolls horizontally instead. */
/** The narrowest a dragged column may go (the Name column keeps its own floor). */
export const COLUMN_MIN_WIDTH = 60;

export function cellStyle(col, override) {
  const base = { textAlign: col.align || 'left' };
  // a width a person dragged wins over the registry; the flexible Name column then
  // becomes a fixed width too, and the table scrolls sideways if it must
  if (Number.isFinite(override)) {
    return { ...base, width: override, flex: 'none', flexShrink: 0, minWidth: 0 };
  }
  if (col.width === 'flex') return { ...base, flex: 1, minWidth: NAME_MIN_WIDTH };
  return { ...base, width: col.width, flexShrink: 0 };
}

/** Minimum pixel width the table needs to lay out the given visible columns
 *  without crushing the Name floor. Applied to the header + scroll body so the
 *  wrapper can scroll them horizontally in lockstep on narrow viewports. */
export function minTableWidth(visibleColumns, widths = {}) {
  let total = TABLE_HPAD + TABLE_CHECKBOX + TABLE_CHEVRON;
  for (const id of visibleColumns) {
    const col = COLUMNS[id];
    if (!col) continue;
    const w = Number.isFinite(widths?.[id]) ? widths[id] : (col.width === 'flex' ? NAME_MIN_WIDTH : col.width);
    total += TABLE_GAP + w;
  }
  return total;
}

/** Default visible set used when no saved prefs / view exist. */
export const DEFAULT_VISIBLE_COLUMNS = COLUMN_ORDER.filter(id => COLUMNS[id]?.defaultVisible);

/** Resolve a raw visibleColumns array to a clean, ordered, locked-included list.
 *  - Drops unknown ids.
 *  - Forces locked columns to be present (Name).
 *  - Returns in COLUMN_ORDER. */
export function resolveVisibleColumns(ids) {
  const requested = new Set(Array.isArray(ids) ? ids : DEFAULT_VISIBLE_COLUMNS);
  for (const id of COLUMN_ORDER) {
    if (COLUMNS[id]?.locked) requested.add(id);
  }
  return COLUMN_ORDER.filter(id => requested.has(id) && COLUMNS[id]);
}
